import { useEffect, useMemo, useRef, useState } from 'react';

const getEntryContextText = (entry = {}) => String(
  entry.note
  || entry.notes
  || entry.sourceText
  || entry.originalData?.sourceText
  || entry.source?.body
  || entry.originalData?.source?.body
  || entry.content
  || entry.originalData?.content
  || entry.text
  || entry.originalData?.text
  || entry.primaryText
  || entry.originalData?.primaryText
  || entry.label
  || entry.originalData?.label
  || entry.titlePrefix
  || entry.originalData?.titlePrefix
  || entry.title
  || entry.originalData?.title
  || ''
).trim();

export const useIncidentCaptureFormState = ({
  mode = 'create',
  entryId = null,
  initialData = null,
  open = false,
  clearError = null,
}) => {
  const [incidentName, setIncidentName] = useState('');
  const [severity, setSeverity] = useState(5);
  const [notes, setNotes] = useState('');
  const [incidentDateTime, setIncidentDateTime] = useState(new Date());
  const [selectedTriggers, setSelectedTriggers] = useState([]);
  const isEditMode = mode === 'edit' || Boolean(entryId || initialData?.id);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (!isEditMode || !initialData) {
      return;
    }

    const incidentDetails = initialData.incidentData || initialData.originalData?.incidentData || initialData.data?.incidentData || {};
    const nextContextText = getEntryContextText(initialData);
    const nextName = String(
      initialData.description
      || incidentDetails.description
      || initialData.title
      || initialData.originalData?.title
      || initialData.notes
      || initialData.originalData?.notes
      || nextContextText
      || ''
    ).trim();
    const nextSeverity = Number(initialData.severity ?? incidentDetails.severity ?? 5);
    const nextNotes = String(
      initialData.triggerNotes
      || initialData.originalData?.triggerNotes
      || incidentDetails.triggerNotes
      || initialData.notes
      || initialData.originalData?.notes
      || incidentDetails.notes
      || nextContextText
      || ''
    ).trim();
    const nextTriggers = Array.isArray(initialData.suspectedTriggers)
      ? initialData.suspectedTriggers
      : Array.isArray(initialData.originalData?.suspectedTriggers)
        ? initialData.originalData.suspectedTriggers
        : Array.isArray(incidentDetails.suspectedTriggers)
          ? incidentDetails.suspectedTriggers
          : [];
    const nextDateTime = initialData.incidentDateTime
      ? new Date(initialData.incidentDateTime)
      : (initialData.timestamp ? new Date(initialData.timestamp) : new Date());

    setIncidentName(nextName);
    setSeverity(Number.isFinite(nextSeverity) ? nextSeverity : 5);
    setNotes(nextNotes);
    setIncidentDateTime(Number.isNaN(nextDateTime.getTime()) ? new Date() : nextDateTime);
    setSelectedTriggers(nextTriggers);
  }, [initialData, isEditMode]);

  useEffect(() => {
    const isOpening = open && !wasOpenRef.current;
    wasOpenRef.current = open;

    if (!isOpening || isEditMode) {
      return;
    }

    setIncidentName('');
    setSeverity(5);
    setNotes('');
    setIncidentDateTime(new Date());
    setSelectedTriggers([]);
    clearError?.();
  }, [clearError, isEditMode, open]);

  const incidentTime = useMemo(
    () => (incidentDateTime instanceof Date ? incidentDateTime : new Date()),
    [incidentDateTime]
  );

  return {
    incidentName,
    setIncidentName,
    severity,
    setSeverity,
    notes,
    setNotes,
    incidentDateTime,
    setIncidentDateTime,
    incidentTime,
    selectedTriggers,
    setSelectedTriggers,
    isEditMode,
  };
};
